/**
 * Какие виды относятся к кадру карты.
 *
 * Считает витрина, а не сервер: вьюпорт в запрос не уходит вовсе. Кадр в
 * запросе попал бы в ключ кэша ответа, и один список видов хранился бы в сотне
 * вариантов — поэтому сервер отдаёт регионы вида и bbox'ы регионов, а
 * пересечение остаётся здесь, в чистой функции без React и без карты.
 *
 * ДОЛГОТА ЗДЕСЬ КРУГОВАЯ. И регион, и кадр записаны одинаково — начало в
 * `[−180, 180)` плюс ширина на восток, — и оба могут переходить через дату:
 * Дальний Восток это `118…202`, а кадр над Беринговым морем — `170…200`. Сравнить
 * такие отрезки числами в лоб нельзя: регион «кончится» на 180, и половина
 * района молча выпадет из фильтра. Ровно эта ошибка уже стоила нам
 * `src/geo/dateline.py` на бэкенде.
 */

import type { MapBBox } from '../map/MapCanvas'
import type { RegionItem, SpeciesItem } from '../api'

const FULL_TURN = 360

/** Долготные дуги пересекаются? Обе заданы началом и шириной на восток. */
function arcsOverlap(aMin: number, aMax: number, bMin: number, bMax: number): boolean {
  const aSpan = Math.min(aMax - aMin, FULL_TURN)
  const bSpan = Math.min(bMax - bMin, FULL_TURN)
  if (aSpan <= 0 || bSpan <= 0) return false
  // Насколько начало B отстоит от начала A, если идти на восток.
  const gap = (((bMin - aMin) % FULL_TURN) + FULL_TURN) % FULL_TURN
  // Либо B начинается внутри A, либо A начинается внутри B (обратный зазор).
  return gap < aSpan || FULL_TURN - gap < bSpan
}

function intersects(bbox: MapBBox, region: RegionItem): boolean {
  const r = region.bbox
  if (bbox.lat_min >= r.lat_max || r.lat_min >= bbox.lat_max) return false
  return arcsOverlap(bbox.lon_min, bbox.lon_max, r.lon_min, r.lon_max)
}

/** Коды регионов, которых касается кадр. */
export function regionsInView(regions: RegionItem[], bbox: MapBBox): Set<string> {
  return new Set(regions.filter(r => intersects(bbox, r)).map(r => r.code))
}

/**
 * Район под ЦЕНТРОМ кадра — тем же правилом, что `pick_region` на сервере.
 *
 * Нужен легенде: с 13.10.2026 тайл каждого района красится своей шкалой, и
 * подписывать надо ту, что под глазами. Спрашивается центр, а не рамка: рамка
 * в запросе попала бы в ключ кэша, и легенда перезапрашивалась бы на каждом
 * сдвиге карты. Мимо всех районов — `null`: подставить первый попавшийся значило
 * бы подписать чужую шкалу.
 */
export function regionAt(
  regions: RegionItem[], lng: number | null, lat: number | null,
): string | null {
  if (lng == null || lat == null) return null
  for (const r of regions) {
    const b = r.bbox
    if (lat < b.lat_min || lat > b.lat_max) continue
    const span = Math.min(b.lon_max - b.lon_min, FULL_TURN)
    const east = (((lng - b.lon_min) % FULL_TURN) + FULL_TURN) % FULL_TURN
    if (east <= span) return r.code
  }
  return null
}

export interface SplitSpecies {
  /** Виды этого района — плюс те, у кого привязки нет вовсе. */
  inView: SpeciesItem[]
  /** Ровно один случай: выбранный вид, которого здесь не бывает. */
  outOfView: SpeciesItem[]
}

/**
 * Разделить список видов по кадру.
 *
 * Три правила молчания, и каждое написано против пустого селектора:
 *  - вид без привязки виден везде («пусто» = «где угодно», а не «нигде»);
 *  - кадр, не задевший ни одного региона, не режет список вовсе;
 *  - список не режется, пока нет кадра или не приехали регионы.
 *
 * Выбранный вид не исчезает никогда: расчёт продолжает считать его, а список
 * лишь честно говорит, что здесь этот вид не водится. Иначе случайный залёт
 * карты через границу стирал бы рабочий контекст и ломал короткие ссылки.
 */
export function splitSpecies(
  species: SpeciesItem[],
  regions: RegionItem[],
  bbox: MapBBox | null,
  selected: number | null,
): SplitSpecies {
  if (!bbox || regions.length === 0) return { inView: species, outOfView: [] }
  const here = regionsInView(regions, bbox)
  if (here.size === 0) return { inView: species, outOfView: [] }

  const inView: SpeciesItem[] = []
  const outOfView: SpeciesItem[] = []
  for (const s of species) {
    const codes = s.regions ?? []
    if (codes.length === 0 || codes.some(c => here.has(c))) inView.push(s)
    else if (s.species_id === selected) outOfView.push(s)
  }
  return { inView, outOfView }
}
